// material ui
import { PaletteMode } from '@mui/material';
import { createTheme } from '@mui/material/styles';

// components
import { dialogCustomization } from '@/theme/Feedback/dialogCustomization';
import { autocompleteCustomization } from '@/theme/Inputs/autocompleteCustomization';
import { buttonCustomization } from '@/theme/Inputs/buttonCustomization';
import { checkboxCustomization } from '@/theme/Inputs/checkboxCustomization';
import { selectCustomization } from '@/theme/Inputs/selectCustomization';
import { textFieldCustomization } from '@/theme/Inputs/textFieldCustomization';
import { toggleButtonCustomization } from '@/theme/Inputs/toggleButtonCustomization';
import { drawerCustomization } from '@/theme/Navigation/drawerCustomization';
import { menuCustomization } from '@/theme/Navigation/menuCustomization';

// relative
import { listCustomization } from './DataDisplay/listCustomization';
import { tooltipCustomization } from './DataDisplay/tooltipCustomization';
import { dataGridCustomization } from './MUIX/dataGridCustomization';
import { cardCustomization } from './Surfaces/cardCustomization';
import { paperCustomization } from './Surfaces/paperCustomization';

const grayscale = {
  0: 'hsl(210 20% 98%)', // paper-white
  50: 'hsl(210 15% 96%)', // near-white
  100: 'hsl(210 15% 93%)',
  200: 'hsl(210 10% 85%)',
  300: 'hsl(210 9% 74%)',
  400: 'hsl(210 8% 62%)',
  500: 'hsl(210 7% 50%)',
  600: 'hsl(210 8% 40%)',
  700: 'hsl(210 9% 30%)',
  800: 'hsl(210 10% 20%)',
  900: 'hsl(210 11% 12%)',
  1000: 'hsl(210 12% 8%)', // softened black
};

export const green = {
  50: '#f1faf4',
  100: '#def4e4',
  200: '#bfe7cb',
  300: '#94d4a7',
  400: '#63ba7d',
  500: '#3d9360',
  600: '#2f744c',
  700: '#23583a',
  800: '#193f2b',
  900: '#10281c',
};

const remoniWheel = {
  0: '#EDEDED',
  100: '#E5E5E5',
  200: '#C6C6C6',
  300: '#9D9D9C',
  400: '#666565',
  500: '#434343',
  600: '#788403',
  700: '#96b100',
  800: '#c2cd00',
  900: '#202D10',
};

const services = {
  home: '#96b100',
  admin: '#17B1A8',
  energyxplorer: '#7B83EB',
  'predictive-heat-control': '#FF6B9D',
  devices: '#2EC97F',
  alarms: '#ff3737',
  integrations: '#FFB800',
  monitor: '#FF8C61',
  weekscheduler: '#9C27B0',
  'support-faq': '#00B8D4',
  analytics: '#FFEA00',
};

const getTheme = (mode: PaletteMode) => {
  const theme = createTheme({
    typography: {
      fontFamily:
        "'Manrope Variable', system-ui, Avenir, Helvetica, Arial, sans-serif",
    },
    palette: {
      mode,
      primary: {
        main: mode === 'dark' ? '#f5f6fa' : '#333333',
        contrastText: mode === 'dark' ? '#333333' : '#f5f6fa',
      },
      remoni: {
        ...remoniWheel,
      },
      grey: {
        ...grayscale,
      },
      green: {
        ...green,
      },
      services,
      brand: {
        main: '#c2cd00',
        light: '#CED733',
        dark: '#878F00',
        contrastText: '#fff',
      },
      status: {
        online: '#44b700',
        degraded: '#f59e0b',
        offline: '#ff5722',
      },
      water: {
        main: '#1E88E5',
        light: '#90CAF9',
        dark: '#1565C0',
        contrastText: '#fff',
      },
      heat: {
        main: '#ff7226',
        light: '#ffb199',
        dark: '#b23c00',
        contrastText: '#000',
      },
      power: {
        main: '#FFC107',
        light: '#FFECB3',
        dark: '#FFA000',
        contrastText: '#000',
      },
      compressedAir: {
        main: '#BBD9E0',
        light: '#E5F1F5',
        dark: '#8FB8C5',
        contrastText: '#000',
      },
      background: {
        default: mode === 'dark' ? grayscale[900] : grayscale[0],
        paper: mode === 'dark' ? grayscale[800] : grayscale[0],
      },
    },
    components: {
      ...dataGridCustomization,
      ...cardCustomization,
      ...paperCustomization,
      ...tooltipCustomization,
      ...listCustomization,
      ...dialogCustomization,
      ...drawerCustomization,
      ...menuCustomization,
      ...toggleButtonCustomization(),
      ...buttonCustomization(),
      ...checkboxCustomization(),
      ...textFieldCustomization(),
      ...autocompleteCustomization(),
      ...selectCustomization(),
    } as const,
  });
  return theme;
};

export default getTheme;
